import { Checkbox } from '@chakra-ui/react';
import type { ColumnDef } from '@tanstack/react-table';

export const getColumns = <TData extends object = object>({
  columns,
  enableRowSelection,
  data,
}: {
  columns?: ColumnDef<TData, unknown>[];
  enableRowSelection?: boolean;
  data: TData[];
}): ColumnDef<TData, unknown>[] => {
  if (columns && columns.length > 0) {
    return enableRowSelection
      ? [
          {
            id: 'select',
            size: 34,
            enableSorting: false,
            enableResizing: false,
            cell: ({ row }) => (
              <Checkbox
                aria-label="Select row"
                isChecked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
                colorScheme="green"
                size="md"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              />
            ),
          },
          ...columns,
        ]
      : columns;
  }

  if (data.length === 0) return [];

  const base: ColumnDef<TData, unknown>[] = Object.keys(data[0] as object)
    .filter((key) => !String(key).startsWith('_'))
    .map((key) => ({
      accessorKey: key,
      header: key,
      cell: (info) => String(info.getValue() ?? ''),
    }));

  if (!enableRowSelection) return base;

  const selectCol: ColumnDef<TData, unknown> = {
    id: 'select',
    size: 34,
    enableSorting: false,
    enableResizing: false,
    cell: ({ row }) => (
      <Checkbox
        aria-label="Select row"
        isChecked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        colorScheme="green"
        size="md"
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      />
    ),
  };

  return [selectCol, ...base];
};

export const resolveTextAlignFn = <TData extends object = object>({
  columns,
}: {
  columns: ColumnDef<TData, unknown>[];
}): ((columnId: string) => 'left' | 'center' | 'right' | undefined) => {
  const alignByColumnId = (() => {
    const map = new Map<string, 'left' | 'center' | 'right'>();
    for (const col of columns) {
      const align = col.meta?.align;
      if (align === undefined) continue;
      const colId = (() => {
        if ('accessorKey' in col && typeof col.accessorKey === 'string') {
          return col.accessorKey;
        }
        return col.id !== undefined ? String(col.id) : '';
      })();
      if (colId !== '') {
        map.set(colId, align);
      }
    }
    return map;
  })();

  return (columnId: string) => {
    const explicit = alignByColumnId.get(columnId);
    if (explicit !== undefined) return explicit;
    return undefined;
  };
};
